import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, Calendar as CalendarIcon, Link2, Trash2, LayoutGrid, List, Search } from 'lucide-react';
import { format } from 'date-fns';

export default function Interviews() {
  const [interviews, setInterviews] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [formData, setFormData] = useState({
    lead_id: '',
    scheduled_date: '',
    meeting_link: '',
    attendees: '',
    notes: '',
  });

  useEffect(() => {
    fetchInterviews();
    fetchLeads();
    fetchContacts();
    fetchUsers();
  }, []);

  const fetchInterviews = async () => {
    try {
      const response = await salesApiClient.get('/interviews');
      setInterviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch interviews');
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await salesApiClient.get('/leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await salesApiClient.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredInterviews = () => {
    return interviews.filter(interview => {
      const leadInfo = getLeadInfo(interview.lead_id).toLowerCase();
      const matchesSearch = searchTerm === '' || 
        leadInfo.includes(searchTerm.toLowerCase()) ||
        interview.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.attendees?.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesUser = filterUser === '' || interview.created_by === filterUser;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const interviewDate = new Date(interview.scheduled_date);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && interviewDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && interviewDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate;
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/interviews', {
        ...formData,
        attendees: formData.attendees.split(',').map((a) => a.trim()),
      });
      toast.success('Interview scheduled successfully');
      setIsDialogOpen(false);
      setFormData({
        lead_id: '',
        scheduled_date: '',
        meeting_link: '',
        attendees: '',
        notes: '',
      });
      fetchInterviews();
    } catch (error) {
      toast.error('Failed to schedule interview');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this interview?')) return;
    try {
      await salesApiClient.delete(`/interviews/${id}`);
      toast.success('Interview deleted');
      fetchInterviews();
    } catch (error) {
      toast.error('Failed to delete interview');
    }
  };

  const getLeadInfo = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return 'Unknown Lead';
    const contact = contacts.find((c) => c.id === lead.contact_id);
    return contact ? `${contact.name} - ${contact.company}` : 'Unknown Contact';
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="interviews-title">
            Interviews
          </h1>
          <p className="text-base text-slate-400 mt-2">Schedule and manage candidate interviews</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-interview-button">
              <Plus size={20} className="mr-2" />
              Schedule Interview
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-interview-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Schedule Interview</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="lead">Lead/Opportunity</Label>
                <select
                  id="lead"
                  value={formData.lead_id}
                  onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                  data-testid="interview-lead-select"
                >
                  <option value="">Select a lead</option>
                  {leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {getLeadInfo(lead.id)} - ${lead.value.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="scheduled_date">Interview Date & Time</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="scheduled_date"
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                  data-testid="interview-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="meeting_link">Meeting Link</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="meeting_link"
                  type="url"
                  value={formData.meeting_link}
                  onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                  placeholder="https://meet.google.com/..."
                  required
                  data-testid="interview-link-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="attendees">Attendees (comma-separated emails)</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="attendees"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  placeholder="john@company.com, jane@company.com"
                  required
                  data-testid="interview-attendees-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="notes">Notes</Label>
                <Textarea className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Interview agenda..."
                  data-testid="interview-notes-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-interview-button">
                Schedule Interview
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input 
            placeholder="Search interviews..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Sales Reps</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')}>
            <LayoutGrid size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')}>
            <List size={18} />
          </Button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Lead/Contact</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Date & Time</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Attendees</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Created By</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredInterviews().map((interview) => (
                <tr key={interview.id} className="border-t border-slate-800 hover:bg-slate-800/50">
                  <td className="p-4">
                    <div className="text-white font-medium">{getLeadInfo(interview.lead_id)}</div>
                  </td>
                  <td className="p-4 text-slate-300">
                    {format(new Date(interview.scheduled_date), 'MMM dd, yyyy h:mm a')}
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {interview.attendees?.slice(0, 2).map((attendee, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">
                          {attendee}
                        </span>
                      ))}
                      {interview.attendees?.length > 2 && (
                        <span className="text-xs text-slate-400">+{interview.attendees.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-slate-400">{getUserName(interview.created_by)}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded ${
                      interview.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      interview.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {interview.status || 'Scheduled'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {interview.meeting_link && (
                        <a href={interview.meeting_link} target="_blank" rel="noopener noreferrer" 
                          className="text-primary hover:underline text-sm">Join</a>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(interview.id)}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {getFilteredInterviews().length === 0 && (
            <div className="text-center py-12 text-slate-500">No interviews found</div>
          )}
        </Card>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="interviews-grid">
        {getFilteredInterviews().map((interview) => (
          <Card
            key={interview.id}
            className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm hover:shadow-md hover:border-slate-700 transition-shadow duration-200 p-6"
            data-testid={`interview-card-${interview.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white" data-testid="interview-lead">
                  {getLeadInfo(interview.lead_id)}
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
                  <CalendarIcon size={16} />
                  <span data-testid="interview-date">
                    {format(new Date(interview.scheduled_date), 'MMM dd, yyyy h:mm a')}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(interview.id)}
                data-testid="delete-interview-button"
              >
                <Trash2 size={18} className="text-red-500" />
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Link2 size={16} className="text-slate-500" />
                <a
                  href={interview.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                  data-testid="interview-link"
                >
                  Join Meeting
                </a>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Attendees
                </p>
                <div className="flex flex-wrap gap-2">
                  {interview.attendees.map((attendee, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-1 bg-slate-100 text-slate-300 rounded"
                      data-testid="interview-attendee"
                    >
                      {attendee}
                    </span>
                  ))}
                </div>
              </div>
              {interview.notes && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                    Notes
                  </p>
                  <p className="text-sm text-slate-400" data-testid="interview-notes">{interview.notes}</p>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      )}

      {getFilteredInterviews().length === 0 && interviews.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center">
          <CalendarIcon size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-400">No interviews match your filters.</p>
        </Card>
      )}

      {interviews.length === 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-interviews">
          <CalendarIcon size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-400">No interviews scheduled. Schedule your first interview.</p>
        </Card>
      )}
    </div>
  );
}
